type CaretPoint = {
    node: Node;
    offset: number;
};

// Resolve the DOM node + offset under a screen point, across browsers.
function caretPointFromScreen(clientX: number, clientY: number): CaretPoint | null {
    const doc = document as Document & {
        caretPositionFromPoint?: (
            x: number,
            y: number,
        ) => { offsetNode: Node; offset: number } | null;
    };

    if (typeof doc.caretPositionFromPoint === 'function') {
        const position = doc.caretPositionFromPoint(clientX, clientY);
        return position ? { node: position.offsetNode, offset: position.offset } : null;
    }

    if (typeof document.caretRangeFromPoint === 'function') {
        const range = document.caretRangeFromPoint(clientX, clientY);
        return range ? { node: range.startContainer, offset: range.startOffset } : null;
    }

    return null;
}

// Number of text characters inside `root` that precede (caretNode, caretOffset).
// Decoration spans (spellcheck, {PREENCHA_AQUI}) don't change text content, so
// this stays stable.
function textOffsetWithin(root: HTMLElement, caretNode: Node, caretOffset: number): number {
    if (caretNode === root || !root.contains(caretNode)) {
        // Caret resolved to the container itself: count text of the first
        // `caretOffset` child nodes.
        let total = 0;
        for (let i = 0; i < caretOffset && i < root.childNodes.length; i += 1) {
            total += root.childNodes[i].textContent?.length ?? 0;
        }
        return total;
    }

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let total = 0;
    let textNode = walker.nextNode();

    while (textNode) {
        if (textNode === caretNode) {
            return total + caretOffset;
        }
        total += textNode.textContent?.length ?? 0;
        textNode = walker.nextNode();
    }

    return total;
}

function isVisibleElement(element: HTMLElement): boolean {
    return getComputedStyle(element).visibility !== 'hidden' && element.getClientRects().length > 0;
}

/**
 * Translate a click within a rendered report section into a section-global text
 * offset (characters from the start of the whole section), so the editor — which
 * edits the entire section — can place the caret and scroll/flash the right
 * paragraph.
 *
 * The read view paginates a section across pages, rendering it as several
 * fragments that share the same `data-editable-section-anchor` (`anchorId`).
 * This sums the text of all earlier fragments plus the local offset within the
 * clicked one — so the anchor must be unique to the edited unit (one block for
 * tests, one section otherwise), never shared across sibling units. Returns
 * null when the point isn't inside `contentEl` or the browser exposes no caret
 * API.
 */
export function getSectionTextOffsetFromPoint(
    contentEl: HTMLElement,
    anchorId: string,
    clientX: number,
    clientY: number,
): number | null {
    const point = caretPointFromScreen(clientX, clientY);

    if (!point || !contentEl.contains(point.node)) {
        return null;
    }

    const localOffset = textOffsetWithin(contentEl, point.node, point.offset);

    // Sum the text of this anchor's earlier page-fragments.
    const scope = contentEl.closest('[data-preview-container]') ?? document;
    const fragments = Array.from(
        scope.querySelectorAll<HTMLElement>(
            `[data-editable-section-anchor="${CSS.escape(anchorId)}"] [data-report-content]`,
        ),
    ).filter(isVisibleElement);

    const index = fragments.indexOf(contentEl);
    let preceding = 0;
    for (let i = 0; i < index; i += 1) {
        preceding += fragments[i].textContent?.length ?? 0;
    }

    return preceding + localOffset;
}
